import React, { useRef, useState } from "react";
import { Button, Form, Image } from "react-bootstrap";
import toast from "react-hot-toast";
import {
  ALLOWED_IMAGE_MIME,
  MAX_IMAGE_BYTES,
  describeSpec,
  validateImageDimensions,
} from "../../utils/imageDims";
import type { ImageSpecEntry } from "../../utils/imageSpecs";

interface ImageUploadFieldProps {
  label?: string;
  spec?: ImageSpecEntry;
  hint?: string;
  accept?: string;
  onUpload: (file: File) => Promise<string | null | undefined>;
  onChange?: (url: string) => void;
  buttonIcon?: string;
  buttonLabel?: string;
  buttonVariant?: string;
  size?: "sm" | "lg";
  disabled?: boolean;
  previewUrl?: string;
  previewWidth?: number | string;
  previewHeight?: number | string;
  showPreview?: boolean;
  fieldId?: string;
}

const ImageUploadField: React.FC<ImageUploadFieldProps> = ({
  label,
  spec,
  hint,
  accept = "image/*",
  onUpload,
  onChange,
  buttonIcon = "bi-upload",
  buttonLabel,
  buttonVariant = "outline-primary",
  size,
  disabled = false,
  previewUrl,
  previewWidth = "100%",
  previewHeight = 120,
  showPreview = true,
  fieldId,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const reactIdRef = useRef<string>(
    `img-up-${Math.random().toString(36).slice(2, 9)}`,
  );
  const inputId = fieldId || reactIdRef.current;
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<{
    ok: boolean;
    width: number;
    height: number;
    message?: string;
  } | null>(null);

  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    // Reset so re-selecting the same file fires onChange again
    e.target.value = "";
    if (!file) return;

    if (!ALLOWED_IMAGE_MIME.includes(file.type)) {
      toast.error(
        "Invalid file type. Please upload JPEG, PNG, GIF, or WebP.",
      );
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error("File size must be less than 5MB.");
      return;
    }

    let validationResult: {
      ok: boolean;
      width: number;
      height: number;
      message?: string;
    } | null = null;

    if (spec) {
      try {
        validationResult = await validateImageDimensions(
          file,
          spec.spec,
          spec.mode,
        );
        if (!validationResult.ok) {
          toast.error(
            validationResult.message ||
              "Image dimensions do not match the recommended size.",
          );
        }
      } catch (err: any) {
        toast.error(err?.message || "Failed to read image dimensions");
        return;
      }
    }

    setResult(validationResult);
    setUploading(true);
    try {
      const url = await onUpload(file);
      if (url) {
        onChange?.(url);
        if (validationResult?.ok) {
          toast.success(
            `Uploaded (${validationResult.width} x ${validationResult.height}px matches recommended size)`,
          );
        }
      }
    } catch (err: any) {
      toast.error(err?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const hintText = hint || spec?.hint;
  const previewStyle: React.CSSProperties = {
    maxWidth:
      typeof previewWidth === "number" ? `${previewWidth}px` : previewWidth,
    maxHeight:
      typeof previewHeight === "number"
        ? `${previewHeight}px`
        : previewHeight,
    objectFit: "cover",
    borderRadius: 6,
  };

  return (
    <div className="image-upload-field">
      {label && (
        <Form.Label className="fw-semibold" htmlFor={inputId}>
          {label}
        </Form.Label>
      )}
      {hintText && (
        <Form.Text className="text-muted d-block mb-2">{hintText}</Form.Text>
      )}
      <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          accept={accept}
          onChange={handleFileChange}
          style={{ display: "none" }}
        />
        <Button
          variant={buttonVariant}
          size={size}
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || uploading}
          title={spec ? `Upload (recommended ${spec.spec.aspectRatio || describeSpec(spec.spec, spec.mode)})` : "Upload"}
        >
          {uploading ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-1"
                role="status"
                aria-hidden="true"
              ></span>
              Uploading...
            </>
          ) : (
            <>
              {buttonIcon && <i className={`bi ${buttonIcon} me-1`}></i>}
              {buttonLabel || "Upload image"}
            </>
          )}
        </Button>
        {result && !uploading && (
          <small
            className={result.ok ? "text-success" : "text-warning"}
            data-testid="image-upload-result"
          >
            <i
              className={`bi ${result.ok ? "bi-check-circle" : "bi-exclamation-triangle"} me-1`}
            ></i>
            Detected: {result.width} x {result.height}px
            {result.ok
              ? " - matches recommended"
              : ` - expected ${spec?.spec.aspectRatio || describeSpec(spec?.spec || {}, spec?.mode || "exact")}`}
          </small>
        )}
      </div>
      {showPreview && previewUrl && (
        <div className="mt-2">
          <Image
            src={previewUrl}
            alt="Preview"
            style={previewStyle}
            onError={(e: any) => {
              e.target.style.display = "none";
            }}
          />
        </div>
      )}
    </div>
  );
};

export default ImageUploadField;
